import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

// ── Styles ─────────────────────────────────────────────────────────
const styles = {
  container: { marginTop: '0' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem'
  },
  title: {
    fontSize: '1.1rem', fontWeight: 700, color: '#1f2937',
    display: 'flex', alignItems: 'center', gap: '0.5rem'
  },
  sectionHeader: {
    fontSize: '0.9rem', fontWeight: 600, color: '#374151',
    padding: '0.5rem 0.75rem', background: '#f3f4f6', borderRadius: '6px',
    marginTop: '1rem', marginBottom: '0.5rem',
    display: 'flex', alignItems: 'center', gap: '0.5rem'
  },
  partRow: {
    display: 'grid', gridTemplateColumns: '2fr 2fr 0.5fr 2fr auto',
    gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0.75rem',
    fontSize: '0.85rem', borderBottom: '1px solid #f3f4f6',
  },
  partRowHeader: {
    display: 'grid', gridTemplateColumns: '2fr 2fr 0.5fr 2fr auto',
    gap: '0.5rem', padding: '0.3rem 0.75rem', fontSize: '0.75rem',
    fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
    letterSpacing: '0.05em', borderBottom: '2px solid #e5e7eb',
  },
  emptyState: {
    textAlign: 'center', padding: '2rem 1rem', color: '#6b7280',
    fontStyle: 'italic', fontSize: '0.9rem',
  },
  addBtn: {
    padding: '0.4rem 0.75rem', fontSize: '0.8rem', fontWeight: 600,
    background: '#2563eb', color: 'white', border: 'none',
    borderRadius: '6px', cursor: 'pointer',
  },
  smallBtn: {
    padding: '0.2rem 0.5rem', fontSize: '0.7rem', border: '1px solid #d1d5db',
    borderRadius: '4px', cursor: 'pointer', background: 'white', color: '#374151',
  },
  dangerBtn: {
    padding: '0.2rem 0.5rem', fontSize: '0.7rem', border: '1px solid #fecaca',
    borderRadius: '4px', cursor: 'pointer', background: '#fff1f2', color: '#dc2626',
  },
  approveBtn: {
    padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: 600,
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    background: '#16a34a', color: 'white',
  },
  rejectBtn: {
    padding: '0.3rem 0.6rem', fontSize: '0.75rem', fontWeight: 600,
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    background: '#dc2626', color: 'white',
  },
  formContainer: {
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
    padding: '1rem', marginTop: '0.75rem', marginBottom: '0.75rem',
  },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' },
  formField: { display: 'flex', flexDirection: 'column', gap: '0.2rem' },
  formLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#4b5563' },
  formInput: {
    padding: '0.4rem 0.6rem', fontSize: '0.85rem', border: '1px solid #d1d5db',
    borderRadius: '5px', outline: 'none',
  },
  badge: {
    fontSize: '0.65rem', fontWeight: 600, padding: '0.15rem 0.4rem',
    borderRadius: '4px', textTransform: 'uppercase',
  },
  pendingBanner: {
    background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '6px',
    padding: '0.6rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.8rem',
    color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem',
    cursor: 'pointer',
  },
  autocompleteList: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    background: 'white', border: '1px solid #d1d5db',
    borderRadius: '0 0 5px 5px', maxHeight: '150px', overflowY: 'auto',
    zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  autocompleteItem: {
    padding: '0.4rem 0.6rem', fontSize: '0.85rem', cursor: 'pointer',
    borderBottom: '1px solid #f3f4f6',
  },
  stagingItem: {
    display: 'grid', gridTemplateColumns: '2fr 2fr 0.5fr 2fr auto',
    gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0.75rem',
    fontSize: '0.85rem', borderBottom: '1px solid #e2e8f0',
    background: '#f0fdf4',
  },
  stagingBadge: {
    fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.3rem',
    borderRadius: '3px', background: '#dcfce7', color: '#166534',
    textTransform: 'uppercase', marginLeft: '0.3rem',
  },
  editBadge: {
    fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.3rem',
    borderRadius: '3px', background: '#dbeafe', color: '#1e40af',
    textTransform: 'uppercase', marginLeft: '0.3rem',
  },
  removeBadge: {
    fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.3rem',
    borderRadius: '3px', background: '#fee2e2', color: '#991b1b',
    textTransform: 'uppercase', marginLeft: '0.3rem',
  },
  submitArea: {
    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
    padding: '1rem', marginTop: '0.75rem',
  },
  submissionCard: {
    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
    padding: '0.75rem', marginBottom: '0.75rem',
  },
  modItemRow: {
    display: 'grid', gridTemplateColumns: '2fr 2fr 0.5fr 2fr auto',
    gap: '0.5rem', alignItems: 'center', padding: '0.35rem 0.5rem',
    fontSize: '0.8rem', borderBottom: '1px solid #fef3c7',
  },
};

// ── Component ──────────────────────────────────────────────────────
const AmpPartsList = ({ amplifierId, isOwner, isAdmin, autoShowPending }) => {
  const { token, isAuthenticated } = useAuth();

  // Approved data
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Moderation
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [showPending, setShowPending] = useState(!!autoShowPending);

  // Staging (local parts before submit)
  const [isContributing, setIsContributing] = useState(false);
  const [stagedParts, setStagedParts] = useState([]);
  const [editingStagedIdx, setEditingStagedIdx] = useState(null);

  // Part form (for adding to staging)
  const [formData, setFormData] = useState({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '', edit_type: 'add', original_part_id: null });

  // Submit dialog
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [editSummary, setEditSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  // Autocomplete
  const [sectionSuggestions, setSectionSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const sectionRef = useRef(null);

  // Moderator inline edit
  const [modEditingItem, setModEditingItem] = useState(null); // {submissionId, itemId, ...fields}
  const [modAddingTo, setModAddingTo] = useState(null); // submissionId
  const [modFormData, setModFormData] = useState({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '' });

  const canModerate = isOwner || isAdmin;

  const authHeaders = useCallback(() => {
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, [token]);

  // ── Fetch Data ──────────────────────────────────────────────────
  const fetchParts = useCallback(async () => {
    try {
      const config = authHeaders();
      const res = await api.get(`/parts-list/${amplifierId}`, config);
      setParts(res.data.parts || []);
    } catch (err) {
      console.error('Error fetching parts:', err);
    }
  }, [amplifierId, authHeaders]);

  const fetchPendingSubmissions = useCallback(async () => {
    if (!isAuthenticated || !canModerate) return;
    try {
      const res = await api.get(`/parts-list/${amplifierId}/submissions/pending`, authHeaders());
      setPendingSubmissions(res.data || []);
    } catch (err) {
      console.error('Error fetching pending submissions:', err);
    }
  }, [amplifierId, isAuthenticated, canModerate, authHeaders]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchParts(), fetchPendingSubmissions()]);
      setLoading(false);
    };
    if (amplifierId) load();
  }, [amplifierId, fetchParts, fetchPendingSubmissions]);

  // ── Section Autocomplete ────────────────────────────────────────
  const fetchSuggestions = useCallback(async (query) => {
    try {
      const res = await api.get(`/parts-list-sections/autocomplete`, { params: { q: query } });
      setSectionSuggestions(res.data || []);
      setShowSuggestions(true);
    } catch (err) {
      setSectionSuggestions([]);
    }
  }, []);

  const handleSectionChange = (val, target) => {
    if (target === 'mod') {
      setModFormData(prev => ({ ...prev, section: val }));
    } else {
      setFormData(prev => ({ ...prev, section: val }));
    }
    if (val.length >= 1) fetchSuggestions(val);
    else fetchSuggestions('');
  };

  const selectSuggestion = (section, target) => {
    if (target === 'mod') {
      setModFormData(prev => ({ ...prev, section }));
    } else {
      setFormData(prev => ({ ...prev, section }));
    }
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handler = (e) => {
      if (sectionRef.current && !sectionRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Staging Handlers ────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '', edit_type: 'add', original_part_id: null });
    setEditingStagedIdx(null);
  };

  const addToStaging = () => {
    if (!formData.section.trim() || !formData.part_name.trim()) {
      setMessage({ type: 'error', text: 'Section and part name are required.' });
      return;
    }
    setMessage(null);

    if (editingStagedIdx !== null) {
      // Update existing staged item
      setStagedParts(prev => prev.map((p, i) => i === editingStagedIdx ? { ...formData } : p));
      setEditingStagedIdx(null);
    } else {
      // Add new
      setStagedParts(prev => [...prev, { ...formData }]);
    }
    resetForm();
  };

  // Stage an edit to an existing approved part
  const stageEditPart = (part) => {
    // Check if we already have a staged change for this part
    const existingIdx = stagedParts.findIndex(p => p.original_part_id === part.part_id);
    if (existingIdx !== -1) {
      // Jump to editing that staged item instead
      editStagedItem(existingIdx);
      return;
    }
    setFormData({
      section: part.section, part_name: part.part_name,
      value_spec: part.value_spec || '', quantity: part.quantity || 1,
      notes: part.notes || '', edit_type: 'modify', original_part_id: part.part_id,
    });
    setEditingStagedIdx(null);
    setMessage({ type: 'info', text: `Editing "${part.part_name}" — modify the fields above and click "Stage Edit".` });
  };

  // Stage a removal of an existing approved part
  const stageRemovePart = (part) => {
    // Check if we already have a staged change for this part
    const existingIdx = stagedParts.findIndex(p => p.original_part_id === part.part_id);
    if (existingIdx !== -1) {
      // Replace with the removal
      setStagedParts(prev => prev.map((p, i) => i === existingIdx
        ? { section: part.section, part_name: part.part_name, value_spec: part.value_spec || '', quantity: part.quantity || 1, notes: part.notes || '', edit_type: 'remove', original_part_id: part.part_id }
        : p
      ));
    } else {
      setStagedParts(prev => [...prev, {
        section: part.section, part_name: part.part_name,
        value_spec: part.value_spec || '', quantity: part.quantity || 1,
        notes: part.notes || '', edit_type: 'remove', original_part_id: part.part_id,
      }]);
    }
    setMessage({ type: 'info', text: `"${part.part_name}" staged for removal.` });
  };

  const editStagedItem = (idx) => {
    setFormData({ ...stagedParts[idx] });
    setEditingStagedIdx(idx);
  };

  const removeStagedItem = (idx) => {
    setStagedParts(prev => prev.filter((_, i) => i !== idx));
    if (editingStagedIdx === idx) resetForm();
  };

  const startContributing = () => {
    setIsContributing(true);
    setStagedParts([]);
    setMessage(null);
    fetchSuggestions('');
  };

  const cancelContributing = () => {
    if (stagedParts.length > 0 && !window.confirm('Discard all staged parts?')) return;
    setIsContributing(false);
    setStagedParts([]);
    setShowSubmitDialog(false);
    setEditSummary('');
    resetForm();
    setMessage(null);
  };

  // ── Submit Batch ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!editSummary.trim()) {
      setMessage({ type: 'error', text: 'Please provide an edit summary describing your contribution.' });
      return;
    }
    setSubmitting(true);
    try {
      const items = stagedParts.map((p, i) => ({
        section: p.section.trim(),
        part_name: p.part_name.trim(),
        value_spec: p.value_spec?.trim() || null,
        quantity: p.quantity || 1,
        notes: p.notes?.trim() || null,
        edit_type: p.edit_type || 'add',
        original_part_id: p.original_part_id || null,
        sort_order: i,
      }));
      const res = await api.post(`/parts-list/${amplifierId}/submit`, { items, edit_summary: editSummary.trim() }, authHeaders());
      setMessage({ type: 'success', text: res.data.message });
      setIsContributing(false);
      setStagedParts([]);
      setShowSubmitDialog(false);
      setEditSummary('');
      resetForm();
      await Promise.all([fetchParts(), fetchPendingSubmissions()]);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to submit parts.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Moderation Handlers ─────────────────────────────────────────
  const handleApprove = async (submissionId) => {
    try {
      const res = await api.post(`/parts-list-submissions/${submissionId}/approve`, {}, authHeaders());
      setMessage({ type: 'success', text: res.data.message });
      await Promise.all([fetchParts(), fetchPendingSubmissions()]);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to approve.' });
    }
  };

  const handleReject = async (submissionId) => {
    const note = window.prompt('Reason for rejection (optional):');
    if (note === null) return; // cancelled
    try {
      const res = await api.post(`/parts-list-submissions/${submissionId}/reject`, { review_note: note || '' }, authHeaders());
      setMessage({ type: 'success', text: res.data.message });
      await Promise.all([fetchParts(), fetchPendingSubmissions()]);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to reject.' });
    }
  };

  const startModEdit = (submissionId, item) => {
    setModEditingItem({ submissionId, itemId: item.item_id });
    setModFormData({
      section: item.section, part_name: item.part_name,
      value_spec: item.value_spec || '', quantity: item.quantity || 1,
      notes: item.notes || ''
    });
  };

  const saveModEdit = async () => {
    if (!modEditingItem) return;
    try {
      await api.put(
        `/parts-list-submissions/${modEditingItem.submissionId}/items/${modEditingItem.itemId}`,
        modFormData, authHeaders()
      );
      setModEditingItem(null);
      await fetchPendingSubmissions();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update item.' });
    }
  };

  const removeModItem = async (submissionId, itemId) => {
    if (!window.confirm('Remove this item from the submission?')) return;
    try {
      const res = await api.delete(`/parts-list-submissions/${submissionId}/items/${itemId}`, authHeaders());
      setMessage({ type: 'success', text: res.data.message });
      await fetchPendingSubmissions();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to remove item.' });
    }
  };

  const startModAdd = (submissionId) => {
    setModAddingTo(submissionId);
    setModFormData({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '' });
    fetchSuggestions('');
  };

  const saveModAdd = async () => {
    if (!modAddingTo || !modFormData.section.trim() || !modFormData.part_name.trim()) {
      setMessage({ type: 'error', text: 'Section and part name are required.' });
      return;
    }
    try {
      await api.post(`/parts-list-submissions/${modAddingTo}/items`, modFormData, authHeaders());
      setModAddingTo(null);
      setModFormData({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '' });
      await fetchPendingSubmissions();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to add item.' });
    }
  };

  // ── Group approved parts by section ─────────────────────────────
  const groupedParts = {};
  for (const part of parts) {
    if (!groupedParts[part.section]) groupedParts[part.section] = [];
    groupedParts[part.section].push(part);
  }
  const sectionNames = Object.keys(groupedParts);

  // ── Group staged parts by section ───────────────────────────────
  const groupedStaged = {};
  stagedParts.forEach((p, idx) => {
    if (!groupedStaged[p.section]) groupedStaged[p.section] = [];
    groupedStaged[p.section].push({ ...p, _idx: idx });
  });

  // ── Render ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.title}>📋 Parts List</div>
        <div style={{ ...styles.emptyState, padding: '1rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          📋 Parts List
          {parts.length > 0 && (
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#6b7280' }}>
              ({parts.length} part{parts.length !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        {isAuthenticated && !isContributing && (
          <button style={styles.addBtn} onClick={startContributing}>
            + Contribute Parts
          </button>
        )}
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.5rem', borderRadius: '6px',
          fontSize: '0.8rem', fontWeight: 500,
          background: message.type === 'success' ? '#dcfce7' : message.type === 'info' ? '#dbeafe' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : message.type === 'info' ? '#1e40af' : '#991b1b',
          border: `1px solid ${message.type === 'success' ? '#86efac' : message.type === 'info' ? '#93c5fd' : '#fecaca'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* ─── Pending Submissions Banner (moderators) ─────────── */}
      {canModerate && pendingSubmissions.length > 0 && (
        <div style={styles.pendingBanner} onClick={() => setShowPending(!showPending)}>
          <span>⏳</span>
          <span>
            <strong>{pendingSubmissions.length}</strong> pending submission{pendingSubmissions.length !== 1 ? 's' : ''} to review
          </span>
          <span style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>{showPending ? '▲ Hide' : '▼ Show'}</span>
        </div>
      )}

      {/* ─── Pending Submission Cards (moderator view) ───────── */}
      {canModerate && showPending && pendingSubmissions.map(sub => (
        <div key={sub.submission_id} style={styles.submissionCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
            <div>
              <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#92400e' }}>
                {sub.proposer_name || 'Unknown'} 
              </span>
              <span style={{ fontSize: '0.8rem', color: '#78716c', marginLeft: '0.3rem' }}>
                — "{sub.edit_summary}"
              </span>
              <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.15rem' }}>
                {new Date(sub.created_at).toLocaleDateString()} · {sub.items?.length || 0} part{(sub.items?.length || 0) !== 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
              <button style={styles.approveBtn} onClick={() => handleApprove(sub.submission_id)}>✓ Approve</button>
              <button style={styles.rejectBtn} onClick={() => handleReject(sub.submission_id)}>✗ Reject</button>
            </div>
          </div>

          {/* Item list within submission */}
          <div style={{ border: '1px solid #fde68a', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ ...styles.partRowHeader, background: '#fef9c3', borderBottom: '1px solid #fde68a' }}>
              <span>Part</span><span>Value / Spec</span><span>Qty</span><span>Notes</span><span></span>
            </div>
            {(sub.items || []).map(item => (
              modEditingItem && modEditingItem.itemId === item.item_id ? (
                /* Inline edit row */
                <div key={item.item_id} style={{ ...styles.modItemRow, background: '#fefce8' }}>
                  <input style={{ ...styles.formInput, fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    value={modFormData.part_name} onChange={e => setModFormData(p => ({ ...p, part_name: e.target.value }))} />
                  <input style={{ ...styles.formInput, fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    value={modFormData.value_spec} onChange={e => setModFormData(p => ({ ...p, value_spec: e.target.value }))} />
                  <input style={{ ...styles.formInput, fontSize: '0.8rem', padding: '0.2rem 0.4rem', width: '40px' }}
                    type="number" min={1} value={modFormData.quantity}
                    onChange={e => setModFormData(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} />
                  <input style={{ ...styles.formInput, fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    value={modFormData.notes} onChange={e => setModFormData(p => ({ ...p, notes: e.target.value }))} />
                  <span style={{ display: 'flex', gap: '0.2rem' }}>
                    <button style={{ ...styles.smallBtn, background: '#dcfce7', border: '1px solid #86efac', fontSize: '0.65rem' }} onClick={saveModEdit}>Save</button>
                    <button style={{ ...styles.smallBtn, fontSize: '0.65rem' }} onClick={() => setModEditingItem(null)}>Cancel</button>
                  </span>
                </div>
              ) : (
                <div key={item.item_id} style={styles.modItemRow}>
                  <span style={{ fontWeight: 500 }}>
                    {item.part_name}
                    <span style={{ fontSize: '0.65rem', color: '#9ca3af', display: 'block' }}>{item.section}</span>
                  </span>
                  <span style={{ color: '#4b5563' }}>{item.value_spec || '—'}</span>
                  <span style={{ color: '#6b7280', textAlign: 'center' }}>{item.quantity}</span>
                  <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{item.notes || '—'}</span>
                  <span style={{ display: 'flex', gap: '0.2rem' }}>
                    <button style={{ ...styles.smallBtn, fontSize: '0.65rem' }} onClick={() => startModEdit(sub.submission_id, item)} title="Edit">✏️</button>
                    <button style={{ ...styles.dangerBtn, fontSize: '0.65rem' }} onClick={() => removeModItem(sub.submission_id, item.item_id)} title="Remove">🗑</button>
                  </span>
                </div>
              )
            ))}

            {/* Moderator add item row */}
            {modAddingTo === sub.submission_id ? (
              <div style={{ ...styles.modItemRow, background: '#f0fdf4' }}>
                <div style={{ position: 'relative' }} ref={sectionRef}>
                  <input style={{ ...styles.formInput, fontSize: '0.8rem', padding: '0.2rem 0.4rem', width: '100%' }}
                    placeholder="Section" value={modFormData.section}
                    onChange={e => handleSectionChange(e.target.value, 'mod')}
                    onFocus={() => fetchSuggestions(modFormData.section)} />
                  {showSuggestions && sectionSuggestions.length > 0 && (
                    <div style={styles.autocompleteList}>
                      {sectionSuggestions.map((s, i) => (
                        <div key={i} style={styles.autocompleteItem}
                          onMouseDown={() => selectSuggestion(s, 'mod')}
                          onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                          onMouseLeave={e => e.target.style.background = 'white'}>{s}</div>
                      ))}
                    </div>
                  )}
                </div>
                <input style={{ ...styles.formInput, fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                  placeholder="Part name" value={modFormData.part_name}
                  onChange={e => setModFormData(p => ({ ...p, part_name: e.target.value }))} />
                <input style={{ ...styles.formInput, fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                  placeholder="Spec" value={modFormData.value_spec}
                  onChange={e => setModFormData(p => ({ ...p, value_spec: e.target.value }))} />
                <span style={{ display: 'flex', gap: '0.2rem' }}>
                  <button style={{ ...styles.smallBtn, background: '#dcfce7', border: '1px solid #86efac', fontSize: '0.65rem' }} onClick={saveModAdd}>Add</button>
                  <button style={{ ...styles.smallBtn, fontSize: '0.65rem' }} onClick={() => setModAddingTo(null)}>Cancel</button>
                </span>
              </div>
            ) : (
              <div style={{ padding: '0.3rem 0.5rem' }}>
                <button style={{ ...styles.smallBtn, fontSize: '0.7rem' }} onClick={() => startModAdd(sub.submission_id)}>
                  + Add Part
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* ─── Contributing Mode (staging area) ───────────────── */}
      {isContributing && (
        <div style={styles.formContainer}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
              {editingStagedIdx !== null ? '✏️ Edit Staged Part' : formData.edit_type === 'modify' ? '✏️ Propose Edit' : '➕ Add Parts'}
              {stagedParts.length > 0 && (
                <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem' }}>
                  ({stagedParts.length} staged)
                </span>
              )}
            </div>
            <button style={{ ...styles.smallBtn, color: '#dc2626' }} onClick={cancelContributing}>
              Cancel
            </button>
          </div>

          {/* Part input form */}
          <div style={styles.formGrid}>
            <div style={{ ...styles.formField, position: 'relative' }} ref={!modAddingTo ? sectionRef : undefined}>
              <label style={styles.formLabel}>Section *</label>
              <input
                style={styles.formInput}
                value={formData.section}
                onChange={e => handleSectionChange(e.target.value)}
                onFocus={() => fetchSuggestions(formData.section)}
                placeholder="e.g. Power Supply"
              />
              {!modAddingTo && showSuggestions && sectionSuggestions.length > 0 && (
                <div style={styles.autocompleteList}>
                  {sectionSuggestions.map((s, i) => (
                    <div key={i} style={styles.autocompleteItem}
                      onMouseDown={() => selectSuggestion(s)}
                      onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                      onMouseLeave={e => e.target.style.background = 'white'}>{s}</div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Part Name *</label>
              <input style={styles.formInput} value={formData.part_name}
                onChange={e => setFormData(p => ({ ...p, part_name: e.target.value }))}
                placeholder="e.g. Filter Capacitors" />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Value / Spec</label>
              <input style={styles.formInput} value={formData.value_spec}
                onChange={e => setFormData(p => ({ ...p, value_spec: e.target.value }))}
                placeholder="e.g. 100uF/500V" />
            </div>

            <div style={styles.formField}>
              <label style={styles.formLabel}>Qty</label>
              <input style={{ ...styles.formInput, width: '60px' }} type="number" min={1}
                value={formData.quantity}
                onChange={e => setFormData(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))} />
            </div>

            <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
              <label style={styles.formLabel}>Notes</label>
              <input style={styles.formInput} value={formData.notes}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="e.g. Electrolytic, axial mount" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
            <button style={styles.addBtn} onClick={addToStaging}>
              {editingStagedIdx !== null ? 'Update Part' : formData.edit_type === 'modify' ? '✏️ Stage Edit' : '+ Add to List'}
            </button>
            {(editingStagedIdx !== null || formData.edit_type === 'modify') && (
              <button style={styles.smallBtn} onClick={resetForm}>Cancel</button>
            )}
          </div>

          {/* Staged parts preview */}
          {stagedParts.length > 0 && (
            <div style={{ marginTop: '0.75rem', border: '1px solid #86efac', borderRadius: '6px', overflow: 'hidden' }}>
              <div style={{ ...styles.partRowHeader, background: '#f0fdf4', borderBottom: '1px solid #86efac' }}>
                <span>Part</span><span>Value / Spec</span><span>Qty</span><span>Notes</span><span></span>
              </div>
              {Object.keys(groupedStaged).map(section => (
                <React.Fragment key={section}>
                  <div style={{ ...styles.sectionHeader, marginTop: 0, borderRadius: 0, background: '#ecfdf5' }}>
                    <span>📁</span> {section}
                  </div>
                  {groupedStaged[section].map(p => {
                    const isRemove = p.edit_type === 'remove';
                    const isModify = p.edit_type === 'modify';
                    const badgeStyle = isRemove ? styles.removeBadge : isModify ? styles.editBadge : styles.stagingBadge;
                    const badgeText = isRemove ? 'remove' : isModify ? 'edit' : 'add';
                    const rowBg = isRemove ? '#fff1f2' : isModify ? '#eff6ff' : '#f0fdf4';
                    return (
                      <div key={p._idx} style={{ ...styles.stagingItem, background: rowBg, textDecoration: isRemove ? 'line-through' : 'none' }}>
                        <span style={{ fontWeight: 500, color: isRemove ? '#991b1b' : '#1f2937' }}>
                          {p.part_name}
                          <span style={badgeStyle}>{badgeText}</span>
                        </span>
                        <span style={{ color: '#4b5563' }}>{p.value_spec || '—'}</span>
                        <span style={{ color: '#6b7280', textAlign: 'center' }}>{p.quantity}</span>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{p.notes || '—'}</span>
                        <span style={{ display: 'flex', gap: '0.25rem' }}>
                          {!isRemove && (
                            <button style={styles.smallBtn} onClick={() => editStagedItem(p._idx)} title="Edit">✏️</button>
                          )}
                          <button style={styles.dangerBtn} onClick={() => removeStagedItem(p._idx)} title="Unstage">✕</button>
                        </span>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* Submit area */}
              {!showSubmitDialog ? (
                <div style={{ padding: '0.6rem 0.75rem', background: '#f0fdf4', borderTop: '1px solid #86efac' }}>
                  <button
                    style={{ ...styles.addBtn, background: '#16a34a' }}
                    onClick={() => setShowSubmitDialog(true)}
                  >
                    Submit {stagedParts.length} Part{stagedParts.length !== 1 ? 's' : ''} for Review
                  </button>
                </div>
              ) : (
                <div style={styles.submitArea}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e40af', marginBottom: '0.4rem' }}>
                    Submit Contribution
                  </div>
                  <div style={styles.formField}>
                    <label style={styles.formLabel}>Edit Summary *</label>
                    <input
                      style={styles.formInput}
                      value={editSummary}
                      onChange={e => setEditSummary(e.target.value)}
                      placeholder="Describe your contribution (e.g., 'Added power supply and preamp parts from service manual')"
                      autoFocus
                    />
                    <span style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.15rem' }}>
                      This note will be visible to moderators reviewing your contribution.
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      style={{ ...styles.addBtn, background: '#16a34a', opacity: submitting ? 0.6 : 1 }}
                      disabled={submitting}
                      onClick={handleSubmit}
                    >
                      {submitting ? 'Submitting...' : 'Submit'}
                    </button>
                    <button style={styles.smallBtn} onClick={() => setShowSubmitDialog(false)}>Back</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Approved Parts Table ─────────────────────────────── */}
      {sectionNames.length === 0 && !isContributing ? (
        <div style={styles.emptyState}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📋</div>
          No parts have been added yet.
          {isAuthenticated && (
            <div style={{ marginTop: '0.3rem', fontStyle: 'normal', fontSize: '0.8rem' }}>
              Be the first to contribute — click <strong>+ Contribute Parts</strong> above!
            </div>
          )}
        </div>
      ) : sectionNames.length > 0 && (
        <div>
          <div style={styles.partRowHeader}>
            <span>Part</span><span>Value / Spec</span><span>Qty</span><span>Notes</span><span></span>
          </div>

          {sectionNames.map(section => (
            <div key={section}>
              <div style={styles.sectionHeader}>
                <span>📁</span> {section}
                <span style={{ fontSize: '0.7rem', fontWeight: 400, color: '#9ca3af', marginLeft: 'auto' }}>
                  {groupedParts[section].length} part{groupedParts[section].length !== 1 ? 's' : ''}
                </span>
              </div>

              {groupedParts[section].map(part => {
                const stagedEntry = stagedParts.find(s => s.original_part_id === part.part_id);
                const isStagedForEdit = stagedEntry && stagedEntry.edit_type === 'modify';
                const isStagedForRemove = stagedEntry && stagedEntry.edit_type === 'remove';
                const rowStyle = {
                  ...styles.partRow,
                  ...(isStagedForRemove ? { background: '#fff1f2', textDecoration: 'line-through', opacity: 0.6 } : {}),
                  ...(isStagedForEdit ? { background: '#eff6ff' } : {}),
                };
                return (
                  <div key={part.part_id} style={rowStyle}>
                    <span style={{ fontWeight: 500, color: isStagedForRemove ? '#991b1b' : '#1f2937' }}>
                      {part.part_name}
                      {isStagedForEdit && <span style={styles.editBadge}>editing</span>}
                      {isStagedForRemove && <span style={styles.removeBadge}>removing</span>}
                    </span>
                    <span style={{ color: '#4b5563' }}>{part.value_spec || '—'}</span>
                    <span style={{ color: '#6b7280', textAlign: 'center' }}>{part.quantity}</span>
                    <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>{part.notes || '—'}</span>
                    <span>
                      {isContributing && !stagedEntry && (
                        <span style={{ display: 'flex', gap: '0.25rem' }}>
                          <button style={styles.smallBtn} onClick={() => stageEditPart(part)} title="Propose edit">✏️</button>
                          <button style={styles.dangerBtn} onClick={() => stageRemovePart(part)} title="Propose removal">🗑</button>
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Footer credit */}
      {parts.length > 0 && (
        <div style={{
          fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.5rem',
          fontStyle: 'italic', textAlign: 'right',
        }}>
          Parts list maintained by the AmpNebula community
        </div>
      )}
    </div>
  );
};

export default AmpPartsList;
